import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import TrapFocus from '@mui/material/Unstable_TrapFocus';
import IconButton from '@mui/material/IconButton';
import { FormattedMessage } from 'react-intl';
import SearchIcon from '@/assets/search.svg?react';
import CancelIcon from '@/assets/cancel3.svg?react';
import TextField from '@/components/TextField/TextField';
import { styled } from '@mui/material/styles';
import {
  KeyboardEvent,
  ReactNode,
  Ref,
  useRef,
  useState,
} from 'react';

type SearchTextFieldProps = {
  className?: string;
  placeholder?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  mode?: 'light' | 'dark';
  small?: boolean;
  'aria-label'?: string;
  loading?: boolean;
  propositions?: ReactNode;
  onEnterUp?: () => void;
  onCancel?: () => void;
  popperRef?: Ref<HTMLDivElement>;
  minQueryLength?: number;
};

const SearchTextField = ({
  className,
  placeholder,
  value,
  onChange,
  mode = 'light',
  small = true,
  loading = false,
  propositions,
  'aria-label': ariaLabel,
  onEnterUp,
  onCancel,
  popperRef,
  minQueryLength = 3,
}: SearchTextFieldProps) => {
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const ignoreNextFocusOpenRef = useRef(false);
  const showPropositions = !loading && !!propositions;
  const hasMinQueryLength = value.trim().length >= minQueryLength;

  const closeOnOptionActivation = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return;
    const option = target.closest('[role="option"]');
    if (!option) return;
    ignoreNextFocusOpenRef.current = true;
    setIsPopperOpen(false);
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onEnterUp && hasMinQueryLength) {
      onEnterUp();
      setIsPopperOpen(false);
    }
    if (event.key === 'Escape' && onCancel) {
      onCancel();
      setIsPopperOpen(false);
    }
  };

  const shouldAttachKeyListener = !!onEnterUp || !!onCancel;

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsPopperOpen(false);
      }}
    >
      <Root className={className}>
        <StyledTextField
          label={
            placeholder || (
              <FormattedMessage id="search" defaultMessage="Search" />
            )
          }
          fullWidth
          size={small ? 'small' : 'medium'}
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            onChange(newValue);
            const meetsMinLength =
              newValue.trim().length >= minQueryLength;
            if (!meetsMinLength) {
              setIsPopperOpen(false);
              return;
            }
            if (!isPopperOpen) {
              setIsPopperOpen(true);
            }
          }}
          onFocus={() => {
            if (ignoreNextFocusOpenRef.current) {
              ignoreNextFocusOpenRef.current = false;
              return;
            }
            if (hasMinQueryLength && (loading || showPropositions)) {
              setIsPopperOpen(true);
            }
          }}
          onKeyUp={shouldAttachKeyListener ? handleKeyUp : undefined}
          slotProps={{
            input: {
              startAdornment: (
                <StyledInputAdornment position="start" mode={mode}>
                  <SearchIcon />
                </StyledInputAdornment>
              ),
            },
          }}
          mode={mode}
          aria-label={ariaLabel || 'Search'}
        />
        {onCancel && value && (
          <ClearButton
            size="small"
            aria-label="Clear search"
            onClick={() => {
              onCancel();
              setIsPopperOpen(false);
            }}
          >
            <CancelIcon />
          </ClearButton>
        )}
        {isPopperOpen &&
          hasMinQueryLength &&
          (loading || showPropositions) && (
            <TrapFocus
              open
              disableAutoFocus
              disableRestoreFocus={false}
              isEnabled={() => true}
            >
              <PopperContainer
                ref={popperRef}
                role={loading ? 'status' : 'listbox'}
                aria-live={loading ? 'polite' : undefined}
                onClick={(event) => {
                  closeOnOptionActivation(event.target);
                }}
                onKeyUp={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ')
                    return;
                  closeOnOptionActivation(event.target);
                }}
              >
                {loading ? (
                  <PopperLoadingContent>
                    <CircularProgress size={16} />
                    <PopperLoadingLabel>
                      <FormattedMessage
                        id="search.loading"
                        defaultMessage="Searching..."
                      />
                    </PopperLoadingLabel>
                  </PopperLoadingContent>
                ) : (
                  propositions
                )}
              </PopperContainer>
            </TrapFocus>
          )}
      </Root>
    </ClickAwayListener>
  );
};

const Root = styled('div')({
  position: 'relative',
  width: '100%',
});

const StyledTextField = styled(TextField)(({ theme, mode }) => ({
  '.MuiInputBase-root': {
    background:
      mode === 'light' ? theme.palette.white : 'transparent',
  },
  '.MuiInputBase-input': {
    paddingRight: theme.spacing(4.5),
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: '10px',
  padding: theme.spacing(0.5),
  color: theme.palette.grey500,
  path: {
    stroke: theme.palette.grey500,
  },
}));

const StyledInputAdornment = styled(InputAdornment)<{
  mode: 'light' | 'dark';
}>(({ theme, mode }) => ({
  background: mode === 'light' ? theme.palette.white : 'transparent',
  path: {
    stroke:
      mode === 'light' ? theme.palette.grey300 : theme.palette.white,
  },
}));

const PopperContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.white,
  borderRadius: theme.radii.sm,
  boxShadow: theme.shadows[2],
  padding: theme.spacing(1.5),
  zIndex: theme.zIndex.modal,
  maxHeight: theme.spacing(36), // ~5 items visible (with current item styling)
  overflowY: 'auto',
  overscrollBehavior: 'contain',
}));

const PopperLoadingContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const PopperLoadingLabel = styled('div')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.grey500,
}));

export default SearchTextField;
